import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectAnalysisMM07, selectGroupAnalysisElements } from '../../state/analysis.selectors';
import { Cr7State, featureKey } from './cr7.state';
import { TranslationUtils } from '../../../shared/utils/translation-utils';
import { selectUserLanguage } from '../../../core/state/core.selectors';
import { DataMM07Part } from '../../models/criterions/DataMM07Part';
import { selectActiveConfigVersion } from '../../../masterdata/state/master-data.selectors';
import { MM07PartType } from '../../../masterdata/models/config-version/MM07PartType';
import { SelectListOption } from '@bmw-ds/components';

const selectCr7State = createFeatureSelector<Cr7State>(featureKey);

export const selectShowForceCasePopup = createSelector(selectCr7State, state => state?.showForceCasePopup);

export const selectAnalysisMM07NoStress = createSelector(selectAnalysisMM07, state => state?.noStress);

export const selectAnalysisMM07ShiValue = createSelector(selectAnalysisMM07, state => state?.shiValue);

export const selectAnalysisMM07ShiRiColor = createSelector(selectAnalysisMM07, state => state?.color);

export const selectAnalysisMM07RiValue = createSelector(selectAnalysisMM07, state => state?.rzValue);

export const selectCR7NioshTableLoading = createSelector(selectCr7State, state => state?.nioshTableLoading);

export const selectCr7Attachments = createSelector(selectAnalysisMM07, state => state?.attachments);

export const selectCR7TableParts = createSelector(selectAnalysisMM07, selectGroupAnalysisElements, (dataMM07, groupAnalysisElements) => {
    if (!dataMM07?.parts || !groupAnalysisElements || groupAnalysisElements.length === 0) {
        return dataMM07?.parts;
    }

    return dataMM07?.parts.map(dataMM07Part => {
        const groupAnalysisElement = groupAnalysisElements.find(element =>
            element?.analysis?.dataMM07?.parts?.some(groupAnalysisPart => arePartsEqual(dataMM07Part, groupAnalysisPart))
        );
        if (groupAnalysisElement) {
            return {
                ...dataMM07Part,
                analysisToRedirect: {
                    analysisKey: groupAnalysisElement.analysis.analysisKey,
                    version: groupAnalysisElement.analysis.version,
                    workplaceNumber: groupAnalysisElement.analysis.workPlaceNumber
                }
            };
        }
        return dataMM07Part;
    });
});

export const selectActiveConfigDefMM07 = createSelector(selectActiveConfigVersion, state => state?.defMM07);

export const selectActiveConfigDefMM07Parts = createSelector(selectActiveConfigDefMM07, state => state?.parts);

export const selectCR7PartsAndDefParts = createSelector(selectCR7TableParts, selectActiveConfigDefMM07Parts, (parts, defMM07Parts) => {
    return { parts: parts, defParts: defMM07Parts };
});

export const selectPullAndPushExecutionConditionDropdownOptions = createSelector(selectActiveConfigDefMM07, selectUserLanguage, (state, userLanguage) => {
    return state?.executionConditions
        .filter(option => option.partType === MM07PartType.PULL_PUSH)
        .map(option => ({ label: TranslationUtils.translateTextValue(option.description, userLanguage), id: option.id.toString() } as SelectListOption));
});

export const selectTransferExecutionConditionDropdownOptions = createSelector(selectActiveConfigDefMM07, selectUserLanguage, (state, userLanguage) => {
    return state?.executionConditions
        .filter(option => option.partType === MM07PartType.TRANSFER)
        .map(option => ({ label: TranslationUtils.translateTextValue(option.description, userLanguage), id: option.id.toString() } as SelectListOption));
});

export const selectPullAndPushDefId = createSelector(
    selectActiveConfigDefMM07,
    state => state?.parts.find(part => part.partType === MM07PartType.PULL_PUSH)!.id
);

export const selectLoadWeightMax = createSelector(selectActiveConfigDefMM07, state => state?.loadWeightMax);

export const selectPostures = createSelector(selectActiveConfigDefMM07, state => state?.postures);

export const selectCR7ActivePart = createSelector(selectAnalysisMM07, state => state?.activePart);

export const selectCR7AttachmentsFromForceCase = createSelector(selectCR7ActivePart, state => state?.attachments);

export const selectCR7NioshTableParts = createSelector(selectAnalysisMM07, state => state?.nioshParts);

export const selectCR7NioshUnit = createSelector(selectAnalysisMM07, state => state?.nioshUnit);

export const selectCR7ActivePartRzValue = createSelector(selectAnalysisMM07, state => state?.activePart?.rzValue);

export const selectCR7ActivePartColor = createSelector(selectAnalysisMM07, state => state?.activePart?.color);

export const selectCR7colorTransfer = createSelector(selectAnalysisMM07, state => state?.colorTransfer);

export const selectCR7colorPullPush = createSelector(selectAnalysisMM07, state => state?.colorPullPush);

function arePartsEqual(analysisPart: DataMM07Part, linkedAnalysisPart: DataMM07Part) {
    return (
        analysisPart.description === linkedAnalysisPart.description &&
        analysisPart.commentPart === linkedAnalysisPart.commentPart &&
        analysisPart.defPartId === linkedAnalysisPart.defPartId &&
        analysisPart.defPostureId === linkedAnalysisPart.defPostureId &&
        analysisPart.loadWeight === linkedAnalysisPart.loadWeight &&
        analysisPart.oneHanded === linkedAnalysisPart.oneHanded &&
        analysisPart.cartWithCasterWheels === linkedAnalysisPart.cartWithCasterWheels &&
        analysisPart.realFrequencyPerAnalysis === linkedAnalysisPart.realFrequencyPerAnalysis &&
        analysisPart.realDistanceShort === linkedAnalysisPart.realDistanceShort &&
        analysisPart.realDistanceLong === linkedAnalysisPart.realDistanceLong &&
        analysisPart.defExecutionConditionId === linkedAnalysisPart.defExecutionConditionId
    );
}

export const selectCR7IsWaitingForRecalc = createSelector(selectCr7State, state => state?.isWaitingForRecalc);
